import { useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

import { api, configureAuth, describeError, fetchConfig } from "./api/client";
import type { Bucket, ConversationSummary, WhoAmI } from "./api/types";
import { getAccount, initAuth, signIn, signOut } from "./auth/msal";
import { BucketDetail } from "./pages/Documents";
import { Buckets } from "./pages/Buckets";
import { Admin } from "./pages/Admin";
import { Guardrails } from "./pages/Guardrails";
import { Chat } from "./pages/Chat";
import { PamesaSplash } from "./components/Loader";
import "./app.css";
import "./components/loader.css";

type Stage =
  | { name: "loading" }
  | { name: "signin" }
  | { name: "ready"; me: WhoAmI; buckets: Bucket[]; authDisabled: boolean }
  | { name: "error"; message: string };

/* Rutas en castellano, como el resto de la interfaz. El identificador de la
   colección va en la URL en lugar del objeto en memoria, así que el detalle se
   resuelve contra la lista ya cargada y una colección borrada redirige al índice
   en vez de quedarse en una vista huérfana. */
const CHAT = "/consultas";
const BUCKETS = "/colecciones";
const ADMIN = "/administracion";
const GUARDRAILS = "/administracion/guardarrailes";

export function App() {
  const [stage, setStage] = useState<Stage>({ name: "loading" });
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const navigate = useNavigate();
  // The open conversation and the current section both come from the URL, so a
  // reload or a pasted link lands in the same place instead of resetting to chat.
  const chatMatch = useMatch(`${CHAT}/:conversationId`);
  const activeConversationId = chatMatch?.params.conversationId ?? null;
  const onBuckets = useMatch(`${BUCKETS}/*`) !== null;
  const onAdmin = useMatch(`${ADMIN}/*`) !== null;
  const [sidebarWidth, setSidebarWidth] = useState<number>(readSidebarWidth);
  const [undo, setUndo] = useState<{ id: string; title: string } | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  /** Sube con cada «+ Nueva consulta»: el chat vuelve a «toda la documentación». */
  const [newChat, setNewChat] = useState(0);
  const undoTimer = useRef<number | null>(null);

  const reloadConversations = useCallback(async () => {
    setConversations(await api.conversations());
  }, []);

  /**
   * Stable identity on purpose: `Chat` lists this in an effect's dependencies, so
   * a fresh arrow on every render would re-run the fetch on every render.
   */
  const conversationMissing = useCallback(() => {
    void navigate(CHAT, { replace: true });
    void reloadConversations().catch(() => undefined);
  }, [navigate, reloadConversations]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    void (async () => {
      try {
        // Config comes from the backend so the tenant and client id live in one
        // place, not compiled into the bundle.
        const config = await fetchConfig();
        configureAuth(config);
        // With the backend's local bypass on there is no app registration to talk
        // to, so MSAL is never initialised: calling it would fail on a client id
        // that does not exist.
        if (!config.authDisabled) {
          await initAuth(config);
          if (!getAccount()) {
            setStage({ name: "signin" });
            return;
          }
        }
        const [me, buckets] = await Promise.all([api.me(), api.buckets()]);
        setStage({ name: "ready", me, buckets, authDisabled: config.authDisabled });
        await api.conversations().then(setConversations);
      } catch (error) {
        setStage({
          name: "error",
          message: error instanceof Error ? error.message : "No se ha podido iniciar.",
        });
      }
    })();
  }, []);

  if (stage.name === "loading") {
    return (
      <Splash>
        <PamesaSplash message="Cargando…" />
      </Splash>
    );
  }

  if (stage.name === "error") {
    return (
      <Splash>
        <p className="login-error">{stage.message}</p>
        <button type="button" className="btn" onClick={() => window.location.reload()}>
          Volver a intentar
        </button>
      </Splash>
    );
  }

  if (stage.name === "signin") {
    return (
      <Splash>
        <h1 className="login-title">Inicia sesión para continuar</h1>
        <p className="login-hint">
          Usa tu cuenta de Microsoft para acceder al asistente documental.
        </p>
        <button type="button" className="btn btn--ms" onClick={() => void signIn()}>
          <MicrosoftLogo />
          Continuar con Microsoft
        </button>
        <p className="login-foot">Acceso restringido al personal autorizado</p>
      </Splash>
    );
  }

  const isAdmin = stage.me.roles.includes("rag.admin");
  const canManage = stage.me.roles.includes("rag.librarian") || isAdmin;

  async function refreshBuckets() {
    // Chat and BucketDetail receive the shell's bucket list; a new collection has
    // to reach them without a full reload. The detail route resolves its bucket
    // from this list on every render, so a deleted one redirects on its own and
    // there is no separate view state to repair.
    const buckets = await api.buckets();
    setStage((current) => (current.name === "ready" ? { ...current, buckets } : current));
  }

  async function removeConversation(conversation: ConversationSummary) {
    try {
      await api.deleteConversation(conversation.id);
    } catch {
      return;
    }
    // Leaving the URL on a deleted conversation would reload into a 404.
    if (activeConversationId === conversation.id) void navigate(CHAT, { replace: true });
    await reloadConversations().catch(() => undefined);
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current);
    setUndo({ id: conversation.id, title: conversation.title });
    undoTimer.current = window.setTimeout(() => setUndo(null), 8000);
  }

  async function undoRemove() {
    if (!undo) return;
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current);
    // The `.catch(() => null)` this replaced turned a failing restore into a
    // silent no-op: the toast closed, the list reloaded without the conversation,
    // and the interface looked exactly like a successful undo. The backend was
    // answering 500 on every call since the feature shipped, and nothing said so.
    // An undo that cannot undo has to say it, because the user's next assumption
    // is that the deletion was permanent.
    try {
      const restored = await api.restoreConversation(undo.id);
      setUndo(null);
      await reloadConversations().catch(() => undefined);
      openConversation(restored.id);
    } catch (error: unknown) {
      setUndo(null);
      setUndoError(describeError(error));
    }
  }

  function openConversation(id: string) {
    // `void`: react-router 7's navigate returns a promise that resolves when the
    // navigation settles. Nothing here depends on that, and the three call sites
    // in this file are the only floating promises the linter finds.
    void navigate(`${CHAT}/${id}`);
  }

  return (
    <div className="shell" style={{ "--sidebar": `${sidebarWidth}px` } as React.CSSProperties}>
      {/* Around 95 focusable controls sit between the document start and the
          content on the chat view — the collection list, the conversation list and
          the sidebar resizer. Without this, reaching the answer by keyboard means
          tabbing through all of them on every navigation. */}
      <a className="skip-link" href="#contenido">
        Ir al contenido
      </a>

      <aside className="sidebar">
        <div className="sidebar-brand">
          {/* La misma imagen que el favicon: isotipo en la pestaña e isotipo en la
              aplicación. El nombre va escrito al lado porque un cuadrado rojo con una
              P lo dice a quien ya lo conoce, y a nadie más. */}
          <img className="sidebar-logo" src="/favicon.svg" alt="" aria-hidden="true" />
          <span className="sidebar-brand-text">
            <strong>Pamesa</strong>
            <small>Asistente RAG</small>
          </span>
        </div>

        <nav className="sidebar-nav sidebar-primary" aria-label="Secciones">
          <button
            type="button"
            className={`sidebar-link${onBuckets || onAdmin ? "" : " is-active"}`}
            onClick={() => navigate(CHAT)}
          >
            Consultas
          </button>
          {/* Solo para quien gestiona colecciones (bibliotecario y administración).
              Quien consulta no ve esta pantalla (decisión de Pamesa, 22/10/2026): la
              API tampoco le da la lista de documentos ni las instrucciones. */}
          {canManage && (
            <button
              type="button"
              className={`sidebar-link${onBuckets ? " is-active" : ""}`}
              onClick={() => navigate(BUCKETS)}
            >
              Colecciones
            </button>
          )}
          {/* Solo para rag.admin. El backend lo exige igualmente en cada ruta de
              /api/admin; esto evita ofrecer un enlace que daría 403. */}
          {isAdmin && (
            <button
              type="button"
              className={`sidebar-link${onAdmin ? " is-active" : ""}`}
              onClick={() => navigate(ADMIN)}
            >
              Administración
            </button>
          )}
        </nav>

        <div className="sidebar-body">
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => {
              setNewChat((n) => n + 1);
              void navigate(CHAT);
            }}
          >
            + Nueva consulta
          </button>
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onOpen={openConversation}
            onDelete={(conversation) => void removeConversation(conversation)}
          />
        </div>

        <div className="sidebar-user">
          <span className="avatar" aria-hidden="true">
            {initials(stage.me.display_name ?? stage.me.email ?? "?")}
          </span>
          <span className="sidebar-user-text">
            <strong>{stage.me.display_name ?? stage.me.email}</strong>
            <small className="sidebar-role">
              {stage.authDisabled ? "Sin autenticación · local" : roleLabel(stage.me.roles)}
            </small>
          </span>
          {/* No session to end when the bypass is on, and signOut() would throw on
              an uninitialised MSAL instance. */}
          {!stage.authDisabled && (
            <button type="button" className="btn btn--quiet" onClick={() => void signOut()}>
              Salir
            </button>
          )}
        </div>

        <div
          className="sidebar-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Ancho del panel lateral"
          aria-valuemin={MIN_SIDEBAR}
          aria-valuemax={MAX_SIDEBAR}
          aria-valuenow={Math.round(sidebarWidth)}
          tabIndex={0}
          title="Arrastra para cambiar el ancho; doble clic para restablecer"
          onPointerDown={(event) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
            setSidebarWidth(clampSidebarWidth(event.clientX));
          }}
          onDoubleClick={() => setSidebarWidth(DEFAULT_SIDEBAR)}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") setSidebarWidth((w) => clampSidebarWidth(w - 16));
            if (event.key === "ArrowRight") setSidebarWidth((w) => clampSidebarWidth(w + 16));
          }}
        />
      </aside>

      <main className="main" id="contenido" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<Navigate to={CHAT} replace />} />
          {[CHAT, `${CHAT}/:conversationId`].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Chat
                  buckets={stage.buckets}
                  conversationId={activeConversationId}
                  onConversationCreated={(conversation) => {
                    // replace, not push: the empty chat and the conversation it
                    // becomes are one step, so Back should leave the chat rather
                    // than return to a blank screen.
                    void navigate(`${CHAT}/${conversation.id}`, { replace: true });
                  }}
                  onListChanged={() => reloadConversations().catch(() => undefined)}
                  onConversationMissing={conversationMissing}
                  showSources={isAdmin}
                  canManage={canManage}
                  resetToken={newChat}
                />
              }
            />
          ))}
          {canManage && (
            <Route
              path={BUCKETS}
              element={
                <Buckets
                  isAdmin={isAdmin}
                  buckets={stage.buckets}
                  onChanged={refreshBuckets}
                  onOpen={(bucket) => navigate(`${BUCKETS}/${bucket.id}`)}
                />
              }
            />
          )}
          {canManage && (
            <Route
              path={`${BUCKETS}/:bucketId`}
              element={
                <BucketRoute
                  buckets={stage.buckets}
                  canManage={canManage}
                  isAdmin={isAdmin}
                  onBack={() => navigate(BUCKETS)}
                  onChanged={refreshBuckets}
                />
              }
            />
          )}
          {isAdmin && <Route path={ADMIN} element={<Admin />} />}
          {isAdmin && <Route path={GUARDRAILS} element={<Guardrails />} />}
          {/* An unknown path is not worth an error screen: the app has one obvious
              landing place. A non-admin who types /administracion lands on chat,
              which is also what the missing route above produces. */}
          <Route path="*" element={<Navigate to={CHAT} replace />} />
        </Routes>
      </main>

      {undo && (
        <div className="undo-toast" role="status">
          <span className="undo-toast-text">Consulta eliminada.</span>
          <button type="button" className="undo-toast-action" onClick={() => void undoRemove()}>
            Deshacer
          </button>
        </div>
      )}

      {undoError !== null && (
        // `alert`, not `status`: this contradicts something the user has just been
        // told, so a screen reader should interrupt rather than queue it.
        <div className="undo-toast undo-toast-error" role="alert">
          <span className="undo-toast-text">No se ha podido recuperar: {undoError}</span>
          <button
            type="button"
            className="undo-toast-action"
            onClick={() => setUndoError(null)}
          >
            Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Resolves `/colecciones/:bucketId` against the list the shell already loaded.
 *
 * The URL carries an id, not the object the previous version passed in memory, so
 * a reload or a pasted link has to look it up. A miss is the normal case after a
 * collection is deleted in another tab, not an error worth a screen of its own:
 * it redirects to the index.
 */
function BucketRoute({
  buckets,
  canManage,
  isAdmin,
  onBack,
  onChanged,
}: {
  buckets: Bucket[];
  canManage: boolean;
  isAdmin: boolean;
  onBack: () => void;
  onChanged: () => Promise<void>;
}) {
  const { bucketId } = useParams();
  const bucket = buckets.find((candidate) => candidate.id === bucketId);
  if (!bucket) return <Navigate to={BUCKETS} replace />;
  return (
    <BucketDetail
      bucket={bucket}
      canManage={canManage}
      isAdmin={isAdmin}
      onBack={onBack}
      onChanged={onChanged}
    />
  );
}

/* La sidebar se redimensiona arrastrando su borde; el ancho sobrevive a la sesión. */
const SIDEBAR_WIDTH_KEY = "pamesa.sidebar-width";
const DEFAULT_SIDEBAR = 256; /* 16rem, el ancho del diseño de Martí */
const MIN_SIDEBAR = 208;
const MAX_SIDEBAR = 440;

function clampSidebarWidth(width: number): number {
  return Math.min(MAX_SIDEBAR, Math.max(MIN_SIDEBAR, width));
}

function readSidebarWidth(): number {
  const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampSidebarWidth(stored) : DEFAULT_SIDEBAR;
}

function ConversationList({
  conversations,
  activeId,
  onOpen,
  onDelete,
}: {
  conversations: ConversationSummary[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onDelete: (conversation: ConversationSummary) => void;
}) {
  if (conversations.length === 0) {
    return <p className="sidebar-empty">Todavía no has preguntado nada.</p>;
  }

  const groups = groupByAge(conversations);
  return (
    <div className="history">
      {groups.map(([label, items]) => (
        <section key={label}>
          <span className="eyebrow sidebar-section">{label}</span>
          <ul className="history-list">
            {items.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  className={`history-item${activeId === conversation.id ? " is-active" : ""}`}
                  onClick={() => onOpen(conversation.id)}
                >
                  <span className="history-label">{conversation.title}</span>
                  <time className="history-date" dateTime={conversation.updated_at}>
                    {formatWhen(conversation.updated_at)}
                  </time>
                </button>
                <button
                  type="button"
                  className="history-delete"
                  aria-label={`Borrar ${conversation.title}`}
                  onClick={() => onDelete(conversation)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function groupByAge(
  conversations: ConversationSummary[],
): Array<[string, ConversationSummary[]]> {
  const today = new Date();
  const startOfDay = (offset: number) => {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    day.setDate(day.getDate() - offset);
    return day.getTime();
  };
  const today_: ConversationSummary[] = [];
  const yesterday: ConversationSummary[] = [];
  const week: ConversationSummary[] = [];
  const older: ConversationSummary[] = [];
  for (const conversation of conversations) {
    const when = new Date(conversation.updated_at).getTime();
    if (when >= startOfDay(0)) today_.push(conversation);
    else if (when >= startOfDay(1)) yesterday.push(conversation);
    else if (when >= startOfDay(7)) week.push(conversation);
    else older.push(conversation);
  }
  const groups: Array<[string, ConversationSummary[]]> = [
    ["Hoy", today_],
    ["Ayer", yesterday],
    ["Últimos 7 días", week],
    ["Anteriores", older],
  ];
  return groups.filter(([, items]) => items.length > 0);
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
}

function initials(name: string): string {
  const parts = name.trim().split(/[\s.@_-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "");
  return letters.join("") || "?";
}

function roleLabel(roles: string[]): string {
  if (roles.includes("rag.admin")) return "Admin";
  if (roles.includes("rag.librarian")) return "Documentalista";
  return "Usuario";
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="splash">
      <div className="login-card">
        <div className="sidebar-brand login-brand">
          {/* Igual que en la barra. La misma imagen que el favicon: isotipo en la pestaña e isotipo en la
              aplicación. El nombre va escrito al lado porque un cuadrado rojo con una
              P lo dice a quien ya lo conoce, y a nadie más. */}
          <img className="sidebar-logo" src="/favicon.svg" alt="" aria-hidden="true" />
          <span className="sidebar-brand-text">
            <strong>Pamesa</strong>
            <small>Asistente RAG</small>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="0" y="0" width="7.5" height="7.5" fill="#f25022" />
      <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7fba00" />
      <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00a4ef" />
      <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#ffb900" />
    </svg>
  );
}
